import React, { useCallback, useContext, useMemo } from 'react';
import styled, { css } from 'styled-components';
import moment from 'moment';
import { required, onlyPositiveNumber } from '@ubeya/shared/utils/form';
import { FlexSpace, FormModal as BaseFormModal, InputField, DateField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { responsiveFields } from '@ubeya/shared-web/style';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useAccountConfig from '@ubeya/shared/hooks/account/useAccountConfig';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import { isMobileOnly } from 'react-device-detect';
import { generatePositionWageValues } from '@ubeya/shared-web/utils/positions';

const Container = styled(FlexSpace)`
  ${responsiveFields.mobile};
`;

const FormModal = styled(BaseFormModal)`
  ${({ $isMobileOnly }) =>
    $isMobileOnly &&
    css`
      max-height: 65vh;
    `}
`;

const WageModal = ({ wage, onClose, onConfirm, baseWage }) => {
  const { t } = useContext(ConfigContext);
  const { currency } = useContext(ConfigContext);
  const { mappedPositions } = usePositions();
  const { account } = useAccountConfig();

  const isFlatWage = account?.payrollConfig?.flatIsActive;

  const defaultPositionWage = useMemo(() => {
    const selectedPosition = mappedPositions[wage?.positionId] || {};

    const selectedWage = generatePositionWageValues({
      wage: selectedPosition.wage,
      wages: selectedPosition.wages,
      externalWage: selectedPosition.clientWage,
      externalWageKey: 'clientWage'
    });

    return selectedWage?.wage || {};
  }, [mappedPositions, wage?.positionId]);

  const wageInitialValues = useMemo(
    () => ({
      ...wage,
      flatHours: wage.flatHours === null || wage.flatHours === 0 ? '' : wage.flatHours,
      hourlyWage: wage.hourlyWage === null || wage.hourlyWage === 0 ? '' : wage.hourlyWage,
      flatWage: wage.flatWage === null || wage.flatWage === 0 ? '' : wage.flatWage
    }),
    [wage]
  );
  const initialValues = useMemo(() => ({ effectiveDate: moment.utc(), ...wageInitialValues }), [wageInitialValues]);

  const handleSubmit = useCallback(
    (values) =>
      onConfirm({
        ...values,
        flatHours: values.flatHours || 0,
        hourlyWage: values.hourlyWage || 0,
        flatWage: values.flatWage || 0,
        effectiveDate: moment(values.effectiveDate).format(API_DATE_FORMAT)
      }),
    [onConfirm]
  );

  return (
    <FormModal
      initialValues={initialValues}
      onSubmit={handleSubmit}
      size="small"
      onClose={onClose}
      title={`${wage.id ? t('editWage') : t('addWage')} - ${mappedPositions[wage.positionId]?.label ?? t('baseWage')}`}
      confirmText={t('save')}
      showCancelButton
      isScrollableContent={false}
      $isMobileOnly={isMobileOnly}
      disabledOnPristine={false}>
      <Container>
        <InputField
          name="hourlyWage"
          type="number"
          title={`${t('hourlyRate')} (${currency})`}
          onChange={onlyPositiveNumber}
          min={0}
          autoFocus
          placeholder={`${t('default')}: ${defaultPositionWage?.hourlyWage || baseWage?.hourlyWage || 0} `}
        />

        {isFlatWage && (
          <>
            <InputField
              name="flatWage"
              title={`${t('flatWage')} (${currency})`}
              onChange={onlyPositiveNumber}
              type="number"
              placeholder={`${t('default')}: ${defaultPositionWage?.flatWage || baseWage?.flatWage || 0}`}
            />
            <InputField
              name="flatHours"
              title={t('flatHours')}
              onChange={onlyPositiveNumber}
              type="number"
              placeholder={`${t('default')}: ${defaultPositionWage?.flatHours || baseWage?.flatHours || 0}`}
            />
          </>
        )}

        <DateField name="effectiveDate" title={t('effectiveFrom')} type="date" validate={required} usePortal />
      </Container>
    </FormModal>
  );
};

export default WageModal;
